<template>
  <section class="bg-white">
    <Head>
      <Title>{{ article.title }}</Title>
      <Meta name="description" hid="description" :content="article.description" />
      <Meta name="og:title" hid="og:title" :content="article.title" />
      <Meta name="og:description" hid="og:description" :content="article.description" />
      <Meta name="og:type" hid="og:type" content="article" />
      <Meta name="og:url" hid="og:url" :content="$env.websiteUrl + $route.path" />
      <Meta name="og:image" hid="og:image" :content="ogImage" />
      <Meta name="og:locale" hid="og:locale" :content="fbCulture" />
      <Meta name="product:price:amount" hid="product:price:amount" content="1.00" />
      <Meta name="product:price:currency" hid="product:price:currency" content="USD" />
      <Meta name="product:brand" hid="product:brand" content="MAJORITY" />
      <Meta name="product:availability" hid="product:availability" content="in stock" />
      <Meta name="product:condition" hid="product:condition" content="new" />
      <Meta name="product:retailer_item_id" hid="product:retailer_item_id" :content="article.id" />
      <Meta name="fb:app_id" hid="fb:app_id" content="517333655736376" />
      <Link
        v-for="(link, index) in hrefLang()"
        :key="index"
        :rel="link.rel"
        :href="link.href"
        :hid="link.hid"
        :hreflang="link.hreflang"
      />
    </Head>

    <div class="container mt-4 mb-6 md:mt-8 md:mb-19 lg:mb-14">
      <CommunityHeader :breadcrumbs="breadcrumbs" />
    </div>

    <article class="community-article-wrapper container relative mb-21 md:mb-24">
      <header class="title">
        <h2
          class="uppercase text-dusty-gray text-tiny leading-3 -tracking-04-em text-center mb-1 md:text-smallest md:leading-3 md:text-left md:mb-2"
        >
          {{ article.category.name }}
        </h2>
        <h1
          class="uppercase font-druk text-display-10 tracking-02-em leading-10 text-center mb-8 md:text-display-14 md:leading-14 md:text-left md:mb-12 lg:text-display-22 lg:leading-22"
        >
          {{ article.title }}
        </h1>
        <figure class="bg-middle-light relative ar-16by9">
          <MajorityUiMajorityImage
            :src="article.featuredImageUrl"
            :alt="article.title"
            :optimized-width="$device.isMobile ? 430 : 640"
            fade-in
            class="object-cover w-full h-full absolute inset-0"
          />
        </figure>
      </header>
      <p
        class="article-limit font-light text-base leading-6 mx-auto md:text-biggest md:leading-9 mb-6 mt-4 md:mb-8 md:mt-8"
      >
        {{ article.description }}
      </p>
      <div
        class="article-content article-limit text-regular md:text-bigger"
        v-html="contentBody"
      />

      <section class="article-limit mx-auto flex flex-col-reverse md:flex-row md:justify-between">
        <div>
          <span
            class="uppercase font-gt-america-extended font-light text-smallest leading-3"
          >{{ $messages('community.labels.share') }}</span>
          <div
            class="flex flex-wrap"
          >
            <a :href="facebookUrl" target="_blank" class="mr-4 last:mr-0 leading-4 md:leading-5 h-10 w-10 bg-bitter-chocolate flex justify-center items-center no-underline">
              <MajorityUiMajorityIcon icon="facebook-simple" class="flex icon text-light-green leading-0 text-bigger" />
            </a>
            <a :href="linkedinLink" target="_blank" class="mr-4 last:mr-0 leading-4 md:leading-5 h-10 w-10 bg-bitter-chocolate flex justify-center items-center no-underline">
              <MajorityUiMajorityIcon icon="linkedin" class="flex icon text-light-green leading-0 text-bigger" />
            </a>
          </div>
        </div>
        <div
          v-if="hasTags"
          class="w-full md:w-5/12 mb-8 md:mb-0"
        >
          <span
            class="uppercase font-gt-america-extended font-light text-smallest leading-3"
          >{{ $messages('community.labels.tags') }}</span>
          <ul
            class="flex flex-wrap"
          >
            <li
              v-for="(tag, index) in article.tags"
              :key="index"
              class="mr-2 last:mr-0 my-1 leading-4 md:leading-5"
            >
              <MajorityUiMajorityLink
                :to="$mRouter.getLocalRoute({ name: 'community-tag-slug', params:{slug: tag.slug}})"
                class="uppercase font-medium tracking-01-em leading-4 text-tiny py-2px px-half-space select-none no-underline bg-bright-yellow hover:bg-dark hover:text-white"
                tracking-name="communityArticleDetailsTag"
                tracking-type="button"
                :text="tag.name"
              />
            </li>
          </ul>
        </div>
      </section>
    </article>

    <CommunityCarouselBlock
      v-if="hasRelatedArticles"
      :title="$messages('community.labels.related_articles')"
      :items="getRelatedArticles"
    />
  </section>
</template>

<script>
import { isNullOrUndefined, isEmpty, isNull } from '@/utils/comparators'
import { optimize } from '@/utils/image-optimization'
import { DATO_IMAGE_TYPES, SUPPORTED_CULTURES } from '@/utils/constants'

export default {
  layout: 'community',
  async setup () {
    try {
      definePageMeta({
        stopDefaultSwitch: true
      })
      const { setLocale } = useI18n()
      const communityClient = useCommunity()
      const locale = useLocale()
      const route = useRoute()
      const article = await communityClient.getArticleBySlug(locale, route.params.slug)
      return {
        article,
        setLocale
      }
    } catch (error) {
      useCustomError(error)
    }
  },

  computed: {
    hasRelatedArticles () {
      return this.article.relatedArticles.length > 0
    },
    getRelatedArticles () {
      if (!this.hasRelatedArticles) { return [] }

      return this.article.relatedArticles.map((x) => {
        return {
          ...x,
          type: 'article',
          imageUrl: x.featuredImageUrl
        }
      })
    },
    facebookUrl () {
      return `https://www.facebook.com/sharer/sharer.php?u=${this.$env.websiteUrl}${this.$route.fullPath}`
    },
    linkedinLink () {
      return `https://www.linkedin.com/sharing/share-offsite/?url=${this.$env.websiteUrl}${this.$route.fullPath}`
    },
    ogImage () {
      return optimize(this.article.featuredImageUrl, false, { w: 640, h: 630 })
    },
    fbCulture () {
      const culture = SUPPORTED_CULTURES.find(culture => culture.twoLetter === this.$i18n.locale)
      if (!isNullOrUndefined(culture)) {
        return culture.fbCulture
      }

      return ''
    },
    contentBody () {
      let body = this.article.body

      const imgUrls = body
        .match(/(http|ftp|https):\/\/([\w_-]+(?:(?:\.[\w_-]+)+))([\w.,@?^=%&:/~+#-]*[\w@?^=%&/~+#-])?/g)

      if (isNull(imgUrls)) {
        return body
      }

      const imgWidth = this.$device.isMobile ? 430 : 640

      imgUrls.filter(l => l.includes('datocms-assets.com') && DATO_IMAGE_TYPES.find(x => l.includes(x))).forEach((url) => {
        body = body.replace(url, optimize(url, false, { w: imgWidth }))
      })

      return body
    },
    breadcrumbs () {
      return [
        {
          title: this.$messages('label.home'),
          to: this.$mRouter.getLocalRoute({ name: 'index' })
        },
        {
          title: this.$messages('page_labels.community'),
          to: this.$mRouter.getLocalRoute({ name: 'community' })
        },
        {
          title: this.article.title
        }
      ]
    },
    hasTags () {
      return !isNullOrUndefined(this.article.tags) && this.article.tags.length > 0
    }
  },
  mounted () {
    this.handleInArticleLinks()
    this.$bus.onDynamicRouteChangeLanguage(this.$route.meta.key, this.handleLocaleSwitch)
  },
  methods: {
    hrefLang () {
      const alternatives = []
      if (this.article && this.article.alternatives) {
        this.article.alternatives.filter(a => !isNullOrUndefined(a.value)).forEach((alternative) => {
          if (alternative.locale === 'en') {
            alternatives.push({
              hid: 'default-alternate',
              rel: 'alternate',
              hreflang: 'x-default',
              href: this.$env.websiteUrl + this.$mRouter.getRouteByLocale({ name: 'community-article-slug', params: { slug: alternative.value } }, alternative.locale)
            })
            alternatives.push({
              hid: 'en-alternate',
              rel: 'alternate',
              hreflang: alternative.locale,
              href: this.$env.websiteUrl + this.$mRouter.getRouteByLocale({ name: 'community-article-slug', params: { slug: alternative.value } }, alternative.locale)
            })
          } else {
            alternatives.push({
              hid: `${alternative.locale}-alternate`,
              rel: 'alternate',
              hreflang: alternative.locale,
              href: this.$env.websiteUrl + this.$mRouter.getRouteByLocale({ name: 'community-article-slug', params: { slug: alternative.value } }, alternative.locale)
            })
          }
        })
      }
      return alternatives
    },
    getAlternativeSlug (locale) {
      const filtered = this.article.alternatives.filter(a => a.locale === locale)
      return filtered.length > 0 ? filtered[0].value : ''
    },
    handleInArticleLinks () {
      const linkElements = document.querySelectorAll('.article-content a[href]') || []

      linkElements.forEach((element) => {
        element.target = '_blank'

        element.onclick = (event) => {
          if (element.href.includes('majority.com')) {
            // Internal links
            this.trackInternalLink(event)
          } else {
            // External links
            this.trackExternalLink(event)
          }
        }
      })
    },
    trackExternalLink (event) {
      const aTag = event.srcElement
      if (!isEmpty(aTag.text)) {
        this.$track.linkClicked('communityContentBodyExternalLink', 'community content', {
          linkText: aTag.text,
          linkTitle: aTag.title,
          linkUrl: aTag.href
        })
      }
    },
    trackInternalLink (event) {
      const aTag = event.srcElement
      if (!isEmpty(aTag.text)) {
        this.$track.linkClicked('communityContentBodyInternalLink', 'community content', {
          linkText: aTag.text,
          linkTitle: aTag.title,
          linkUrl: aTag.href
        })
      }
    },
    handleLocaleSwitch (val) {
      const slug = this.getAlternativeSlug(val.destinationLocale)
      this.setLocale(val.destinationLocale).then(() => {
        window.location.href = this.$mRouter.getLocalRoute({ name: 'community-article-slug', params: { slug } }).href
      })
    }
  }
}
</script>

    <style lang="postcss" scoped>
    .title {
      @apply mx-auto;
      max-width: 870px;
    }

    .article-limit {
      width: 100%;

      @screen lg {
        max-width: 740px;
      }
    }

    :deep(.article-content) {
      @apply mx-auto text-smallest;
      iframe {
        max-width: 100%;
      }
      img {
        width: 100%;
        display: block;
        margin: auto;
      }

      a {
        @apply font-bold !important;
        word-wrap: break-word;
        span {
          @apply font-bold !important;
        }
      }
      a[href] {
        word-wrap: break-word;
        text-decoration: underline;
      }

      ul ,ol, a, p {
        @apply text-smaller;
      }
      p {
        @apply mb-6;
      }
      h1 {
        @apply font-gt-america font-bold text-base leading-6 mb-tiny opacity-100 normal-case;
      }
      h2 {
        @apply font-gt-america font-normal text-base leading-6 mb-tiny opacity-100 normal-case;
      }
      h3 {
        @apply font-gt-america-extended font-light mb-tiny text-smaller leading-6 uppercase;
      }

      @screen md {
        ul ,ol, a, p {
          @apply text-display-5 leading-8;
        }
        p {
          @apply mb-8;
        }
        h1 {
          @apply mb-6 text-biggest leading-8;
        }
        h2 {
          @apply mb-6 text-biggest leading-8;
        }
        h3 {
          @apply mb-6 text-base;
        }
      }

      @screen lg {
        p {
          @apply mb-12;
        }
      }

      ul {
        @apply list-inside list-disc mb-tiny;
      }
      ol {
        @apply list-inside list-decimal mb-tiny;
      }
      li {
        > ul, > ol {
          @apply pl-large
        }
        ul {
          @apply list-circle;
          li {
            ul {
              @apply list-square
            }
          }
        }
      }
    }

    </style>
